import React, { FC, memo, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { PreviewPlayerContext } from 'contexts/PreviewPlayerContext'
import { NWShortCutContext } from 'contexts/NWShortCutContext'
import type Konva from 'konva'
import { useSelector } from 'react-redux'
import { selectIsResizingEditor } from 'selectors/useInterface'
import { createImage } from '../features/konva-shapes/createImage'
import { getReferenceVideoAsset } from '../selectors'
import { waitFrame } from '../Util'

export const PreviewPlayerContextProvider: FC = memo(({ children }) => {
  const [ captureScale, setCaptureScale ] = useState<number>(1)
  const [ canvasSource, setCanvasSource ] = useState<Konva.Stage | undefined>(undefined)
  const [ imageSource, setImageSource ] = useState<HTMLImageElement | undefined>(undefined)
  const [ videoSource, setVideoSource ] = useState<HTMLVideoElement | undefined>(undefined)
  const [ previewPlayer, setPreviewPlayer ] = useState<HTMLDivElement | null>(null)

  const isResizing = useSelector(selectIsResizingEditor)
  const referenceAsset = useSelector(getReferenceVideoAsset)

  const previewPlayerBox = useRef<HTMLDivElement | null>(null)

  const onSetPreviewPlayer: React.RefCallback<HTMLDivElement> = useCallback(ref => {
    setPreviewPlayer(ref)
  }, [ setPreviewPlayer ])

  const { registerNWGlobalHotKey, unRegisterNWGlobalHotKey } = useContext(NWShortCutContext)

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen()
      unRegisterNWGlobalHotKey()
    } else if (previewPlayer) {
      previewPlayer.requestFullscreen()
      registerNWGlobalHotKey()
    }
  }, [ registerNWGlobalHotKey, unRegisterNWGlobalHotKey, previewPlayer ])

  const saveFrame = (data: string, fileName = 'frame') => {
    const link = document.createElement('a')
    link.href = data
    link.download = fileName
    link.click()
    URL.revokeObjectURL(link.href)
    link.remove()
  }

  const captureFrame = useCallback((isMedia: boolean) => {
    if (isMedia) {
      if (videoSource) {
        const canvas = document.createElement('canvas')
        canvas.width = videoSource.videoWidth
        canvas.height = videoSource.videoHeight
        const ctx = canvas.getContext('2d')

        if (ctx) {
          ctx.drawImage(videoSource, 0, 0, canvas.width, canvas.height)
          window.requestAnimationFrame(() => {
            saveFrame(canvas.toDataURL('image/jpeg', 0.9), 'source-video-frame.jpg')
            canvas.remove()
          })
        }
      }

      // if (imageSource?.src) {
      //   fetch(imageSource.src)
      //     .then(response => response.blob())
      //     .then(blob => new Promise<string>((resolve, reject) => {
      //       const reader = new FileReader()
      //       reader.onloadend = () => resolve(reader.result as string)
      //       reader.onerror = reject
      //       reader.readAsDataURL(blob)
      //     }))
      //     .then(data => saveFrame(data, 'image-source.jpeg'))
      // }
      if (imageSource) {
        const size = {
          width: imageSource.naturalWidth,
          height: imageSource.naturalHeight,
        }
        createImage(imageSource.src, { size, opacity: 1, outlineWidth: undefined, outlineColor: undefined })
          .then((shape: Konva.Image) => {
            saveFrame(shape.toDataURL({ mimeType: 'image/jpeg', quality: 0.9 }), 'source-image.jpg')
          })
      }
    }

    if (!isMedia && canvasSource) {
      // eslint-disable-next-line no-underscore-dangle
      const isHidden = (layer: Konva.Layer) => layer.getCanvas()._canvas.style.visibility === 'hidden'
      const layers = (Object.values(canvasSource.getLayers()) as Konva.Layer[])
        .filter(layer => layer.name && layer.name() !== 'transition') as Konva.Layer[]

      setCaptureScale((referenceAsset?.width || 0) / canvasSource.width())

      waitFrame(3)
        .then(() => layers.forEach(layer => isHidden(layer) && layer.hide()))
        .then(() => waitFrame())
        .then(() => saveFrame(canvasSource.toDataURL({ mimeType: 'image/jpeg', quality: 0.9 }), 'timeline-frame.jpg'))
        .then(() => {
          setCaptureScale(1)
          layers.forEach(layer => isHidden(layer) && layer.show())
        })
    }
  }, [ videoSource, canvasSource, imageSource, referenceAsset ])

  const previewPlayerBounds = useMemo(() => (
    previewPlayer?.getBoundingClientRect()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [ previewPlayer, isResizing ])

  const values = useMemo(() => ({
    previewPlayer,
    previewPlayerBox,
    onSetPreviewPlayer,
    imageSource,
    canvasSource,
    videoSource,
    captureScale,
    setImageSource,
    setCanvasSource,
    setVideoSource,
    handleFullscreen,
    captureFrame,
    previewPlayerBounds,
  }), [
    previewPlayer,
    previewPlayerBox,
    onSetPreviewPlayer,
    imageSource,
    canvasSource,
    videoSource,
    captureScale,
    setImageSource,
    setCanvasSource,
    setVideoSource,
    handleFullscreen,
    captureFrame,
    previewPlayerBounds,
  ])

  return (
    <PreviewPlayerContext.Provider value={values}>
      {children}
    </PreviewPlayerContext.Provider>
  )
})
